import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from './MobileApp';
import { mobileApi } from '../../services/api';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';
import MobileNav from '../../components/mobile/MobileNav';
import MobileHeader from '../../components/mobile/MobileHeader';
import MobileCard from '../../components/mobile/MobileCard';
import { Play, ChevronRight, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const PAGE = 20;

const formatTime = (s) => {
  if (!s) return '00:00:00';
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), c = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(c).padStart(2, '0')}`;
};
const overdueDays = (wo) => {
  if (!wo.scheduled_date || wo.status === 'completed') return null;
  const d = Math.floor((Date.now() - new Date(wo.scheduled_date).getTime()) / 86400000);
  return d > 0 ? d : null;
};
const workTypeLabel = (wo) => {
  const isRepair = wo.defect_id || (wo.linked_defect_ids && wo.linked_defect_ids.length > 0) || /^repair/i.test(wo.title || '');
  if (isRepair) return 'Repair';
  if (/statutory|thorough|annual/i.test(wo.title || '')) return 'Statutory';
  return 'Planned';
};

const CHIP = {
  urgent: 'bg-[var(--critical-bg)] text-[var(--critical-ink)] border-[var(--critical-edge)]',
  high: 'bg-[var(--overdue-bg)] text-[var(--overdue-ink)] border-[var(--overdue-edge)]',
  progress: 'bg-[var(--volt-tint)] text-[var(--volt-ink)] border-[var(--volt-edge)]',
};
const Chip = ({ tone, children }) => (
  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-[4px] text-[11px] font-semibold border ${CHIP[tone]}`}>{children}</span>
);

const WorkOrderRow = ({ wo }) => {
  const navigate = useNavigate();
  const [elapsed, setElapsed] = useState(wo.total_time_seconds || 0);
  useEffect(() => {
    if (wo.timer_is_running && wo.timer_started_at) {
      const start = new Date(wo.timer_started_at);
      const iv = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000) + (wo.total_time_seconds || 0)), 1000);
      return () => clearInterval(iv);
    }
  }, [wo.timer_is_running, wo.timer_started_at, wo.total_time_seconds]);

  const od = overdueDays(wo);
  const running = wo.timer_is_running;
  const done = wo.status === 'completed';
  const priority = (wo.priority || '').toLowerCase();
  const spine = done ? 'var(--pass)' : running ? 'var(--volt-edge)' : od ? 'var(--overdue)' : 'var(--border)';
  const asset = wo.asset_unit_number || wo.asset_name || '—';

  return (
    <MobileCard spine={spine} onClick={() => navigate(`/mobile/work-orders/${wo.id}`)} className="mb-2.5" padding="normal">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-mono text-[14px] font-bold text-[var(--ink-primary)] truncate">{asset}</span>
              {running && <Chip tone="progress">In progress</Chip>}
              {!running && priority === 'urgent' && <Chip tone="urgent">URGENT</Chip>}
              {!running && priority === 'high' && <Chip tone="high">HIGH</Chip>}
            </div>
            {running && <span className="font-mono text-[13px] font-bold text-[var(--ink-primary)] shrink-0">{formatTime(elapsed)}</span>}
          </div>
          <div className="text-[15px] font-medium text-[var(--ink-primary)] truncate mt-1">{wo.title}</div>
          <div className="flex items-center gap-1.5 mt-1.5 text-[13px] min-w-0">
            {done ? (
              <span className="text-[var(--pass-ink)] shrink-0">Completed</span>
            ) : od ? (
              <>
                <span className="shrink-0 rounded-full" style={{ width: 6, height: 6, background: 'var(--overdue)' }} />
                <span className="font-semibold text-[var(--overdue-ink)] shrink-0">Overdue {od} {od === 1 ? 'day' : 'days'}</span>
              </>
            ) : (
              <span className="text-[var(--ink-tertiary)] shrink-0">Scheduled</span>
            )}
            <span className="text-[var(--ink-tertiary)] truncate">· {workTypeLabel(wo)}</span>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-[var(--ink-tertiary)] shrink-0" />
      </div>
    </MobileCard>
  );
};

const GroupHeader = ({ label, tone }) => (
  <div className="flex items-center gap-3 mb-3 mt-1">
    <span className="text-[11px] font-bold uppercase tracking-[0.11em]" style={{ color: tone === 'overdue' ? 'var(--overdue-ink)' : '#6E6A61' }}>{label}</span>
    <div className="h-px flex-1 bg-[var(--border)]" />
  </div>
);

const MobileWorkOrders = () => {
  const { getToken } = useMobileAuth();
  const [filter, setFilter] = useState('active');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ active: 0, done: 0, all: 0 });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const offsetRef = useRef(0);

  const fetchPage = useCallback(async (f, offset, replace) => {
    try {
      const res = await mobileApi.getWorkOrders(getToken(), f, PAGE, offset);
      const data = res.data;
      setCounts(data.counts || { active: 0, done: 0, all: 0 });
      setTotal(data.total || 0);
      setItems((prev) => replace ? (data.items || []) : [...prev, ...(data.items || [])]);
      offsetRef.current = offset + (data.items || []).length;
    } catch { toast.error('Failed to load work orders'); }
  }, [getToken]);

  useEffect(() => {
    setLoading(true);
    offsetRef.current = 0;
    fetchPage(filter, 0, true).finally(() => setLoading(false));
  }, [filter, fetchPage]);

  const hasMore = items.length < total;
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    await fetchPage(filter, offsetRef.current, false);
    setLoadingMore(false);
  }, [loadingMore, hasMore, fetchPage, filter]);
  const sentinelRef = useInfiniteScroll(hasMore, loading || loadingMore, loadMore);

  const tabs = [{ key: 'active', label: 'Active' }, { key: 'done', label: 'Done' }, { key: 'all', label: 'All' }];

  // Inline group headers (works across pages because the server order is stable).
  const rendered = [];
  let lastGroup = null;
  items.forEach((wo) => {
    const done = wo.status === 'completed';
    const group = done ? 'done' : (wo.timer_is_running || overdueDays(wo)) ? 'overdue' : 'scheduled';
    if (filter !== 'done' && group !== lastGroup) {
      const label = group === 'overdue' ? 'Overdue' : group === 'scheduled' ? 'Scheduled' : 'Done';
      rendered.push(<GroupHeader key={`h-${wo.id}`} label={label} tone={group === 'overdue' ? 'overdue' : 'neutral'} />);
      lastGroup = group;
    }
    rendered.push(<WorkOrderRow key={wo.id} wo={wo} />);
  });

  return (
    <div className="min-h-screen bg-[var(--surface-page)] pb-24">
      <MobileHeader title="My work orders" showBack />

      <div className="px-4 py-4">
        <div className="flex gap-1 p-1 rounded-[12px] bg-[var(--surface-sunken)] border border-[var(--border)]" style={{ height: 44 }}>
          {tabs.map((t) => {
            const sel = filter === t.key;
            return (
              <button key={t.key} onClick={() => setFilter(t.key)} data-testid={`wo-tab-${t.key}`}
                className={`flex-1 rounded-[9px] flex items-center justify-center gap-1.5 text-[13px] transition-colors ${sel ? 'bg-white font-bold text-[var(--ink-primary)]' : 'text-[var(--ink-secondary)] font-medium'}`}
                style={sel ? { border: '1px solid #C4BFB3' } : {}}>
                {t.label}<span className="font-mono text-[11px] text-[var(--ink-tertiary)]">{counts[t.key]}</span>
              </button>
            );
          })}
        </div>

        {filter !== 'done' && !loading && items.length > 0 && (
          <div className="flex items-center justify-between mt-4 mb-1">
            <span className="text-[12.5px] text-[var(--ink-secondary)]">Sorted <b className="text-[var(--ink-primary)]">most overdue first</b></span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[var(--volt-icon)] animate-spin" /></div>
        ) : items.length === 0 ? (
          <MobileCard className="text-center py-8 mt-4">
            <CheckCircle2 className="w-10 h-10 text-[var(--pass-ink)] mx-auto mb-2" />
            <p className="text-[var(--ink-tertiary)]">No work orders here</p>
          </MobileCard>
        ) : (
          <div className="mt-4">
            {rendered}
            <div ref={sentinelRef} />
            {loadingMore && <div className="flex justify-center py-4"><Loader2 className="w-6 h-6 text-[var(--volt-icon)] animate-spin" /></div>}
            {!hasMore && items.length > 0 && <p className="text-center text-[12px] text-[var(--ink-tertiary)] py-3">{total} work order{total === 1 ? '' : 's'}</p>}
          </div>
        )}
      </div>

      <MobileNav />
    </div>
  );
};

export default MobileWorkOrders;
